import React, {useEffect, useState} from 'react';
import LineColumnChart from "../LineColumnChart";
import RevenueTable from "../RevenueTable";
import {addPrefix} from "../../../utils/UriUtil";
import {jsonResponseHandler, revenueResponseHandler} from "../../../utils/FetchHelper";
import {useDispatch} from "react-redux";
import moment from 'moment';

const RevenueDetail = () => {
  const getBaseDate = () => {
    const now = new Date();
    return now.getHours() < 10 ?
      moment(now).startOf('day').subtract(2,'days').toDate() :
      moment(now).startOf('day').subtract(1,'days').toDate();
  };

  const dispatch = useDispatch();
  const [lineOn, setLineOn] = useState(true);
  const [columnOn, setColumnOn] = useState(true);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [activeDate, setActiveDate] = useState(getBaseDate());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [granularity, setGranularity] = useState("day");
  const [graphData, setGraphData] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [clickableRight, setClickableRight] = useState(false);
  const [graphError, setGraphError] = useState(false);
  const [tableError, setTableError] = useState(false);

  const fetchGraphData = (granularity, start, end) => {
    setGraphData(null);
    setGraphError(false);
    const baseDate = getBaseDate();
    const startDate = moment(start).format('YYYY-MM-DD');
    const endDate = baseDate < end ? moment(baseDate).format('YYYY-MM-DD') : moment(end).format('YYYY-MM-DD');
    fetch(addPrefix(`/manage/revenue/trend/adfit.json?granularity=${granularity}&startDate=${startDate}&endDate=${endDate}`), {
      credentials: "include",
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(jsonResponseHandler(dispatch))
      .then(revenueResponseHandler(dispatch))
      .then(r => r.json())
      .then(data => {
        if (data && data.data) {
          const rawData = JSON.parse(JSON.stringify(data.data));
          rawData.forEach((data) => {
            data.lineValue = data.value.earnings ?? 0;
            data.columnValue = data.value.impression ?? 0;
            delete data.value;
          });
          setGraphData(rawData);
        }
      })
      .catch(e => {
        console.error(e);
        setGraphError(true);
      });
  };

  const fetchTableData = (date, granularity) => {
    setTableLoading(true);
    setTableError(false);
    let endDate;
    if (granularity === 'day') {
      endDate = date
    } else if (granularity === 'week') {
      endDate = moment(date).add(6, 'days').toDate();
    } else if (granularity === 'month') {
      endDate = moment(date).add(1, 'months').subtract(1, 'days').toDate();
    }
    const baseDate = getBaseDate();
    const start = moment(date).format('YYYY-MM-DD');
    const end = baseDate < endDate ? moment(baseDate).format('YYYY-MM-DD') : moment(endDate).format('YYYY-MM-DD');
    fetch(addPrefix(`/manage/revenue/info/adfit.json?granularity=${granularity}&startDate=${start}&endDate=${end}`), {
      credentials: "include",
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(jsonResponseHandler(dispatch))
      .then(revenueResponseHandler(dispatch))
      .then(r => r.json())
      .then(data => {
        setTableData(data.data);
        setTableLoading(false);
      })
      .catch(e => {
        console.error(e);
        setTableError(true);
      });
  };

  const lineToggle = () => {
    setLineOn(!lineOn);
  };
  const columnToggle = () => {
    setColumnOn(!columnOn);
  };
  const handleClickDate = (date) => {
    setActiveDate(date);
  };
  const handleClickLeft = () => {
    let newStartDate;
    let newEndDate;
    let newActiveDate;
    if (granularity === 'day') {
      newStartDate = moment(startDate).subtract(21, 'days').toDate();
      newEndDate = moment(newStartDate).add(20, 'days').toDate();
      newActiveDate = newEndDate;
    } else if (granularity === 'week') {
      newStartDate = moment(startDate).subtract(14, 'weeks').toDate();
      newEndDate = moment(newStartDate).add(13, 'weeks').endOf('isoweek').toDate();
      newActiveDate = moment(newEndDate).startOf('isoweek').toDate();
    } else if (granularity === 'month') {
      newStartDate = moment(startDate).subtract(12, 'months').toDate();
      newEndDate = moment(newStartDate).add(11, 'months').endOf('month').toDate();
      newActiveDate = moment(newEndDate).startOf('month').toDate();
    }
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    fetchGraphData(granularity, newStartDate, newEndDate);
    setActiveDate(newActiveDate);
  };
  const handleClickRight = () => {
    if (!clickableRight) {
      return;
    }
    let newStartDate;
    let newEndDate;
    let newActiveDate;
    if (granularity === 'day') {
      newStartDate = moment(endDate).add(1, 'days').toDate();
      newEndDate = moment(newStartDate).add(20, 'days').toDate();
      newActiveDate = newEndDate;
    } else if (granularity === 'week') {
      newStartDate = moment(endDate).add(1, 'days').toDate();
      newEndDate = moment(newStartDate).add(13, 'weeks').endOf('isoweek').toDate();
      newActiveDate = moment(newEndDate).startOf('isoweek').toDate();
    } else if (granularity === 'month') {
      newStartDate = moment(endDate).add(1, 'days').toDate();
      newEndDate = moment(newStartDate).add(11, 'months').endOf('month').toDate();
      newActiveDate = moment(newEndDate).startOf('month').toDate();
    }
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    fetchGraphData(granularity, newStartDate, newEndDate);
    setActiveDate(newActiveDate);
  };
  const setDay = () => {
    setGranularity("day");
    const baseDate = getBaseDate();
    const before20Days = moment(baseDate).subtract(20, 'days').toDate();
    setStartDate(before20Days);
    setEndDate(baseDate);
    fetchGraphData('day', before20Days, baseDate);
    setActiveDate(baseDate);
  };
  const setWeek = () => {
    setGranularity("week");
    const baseDate = getBaseDate();
    const thisMonday = moment(baseDate).startOf('isoweek').toDate();
    const before13Weeks = moment(thisMonday).subtract(13, 'week').toDate();
    const thisSunday = moment(baseDate).endOf('isoweek').toDate();
    setStartDate(before13Weeks);
    setEndDate(thisSunday);
    fetchGraphData('week', before13Weeks, thisSunday);
    setActiveDate(thisMonday);
  };
  const setMonth = () => {
    setGranularity("month");
    const baseDate = getBaseDate();
    const firstDayOfThisMonth = moment(baseDate).startOf('month').toDate();
    const lastDayOfThisMonth = moment(baseDate).endOf('month').toDate();
    const before11Months = moment(firstDayOfThisMonth).subtract(11, 'months');
    setStartDate(before11Months);
    setEndDate(lastDayOfThisMonth);
    fetchGraphData('month', before11Months, lastDayOfThisMonth);
    setActiveDate(firstDayOfThisMonth);
  };

  const handleEnterOutside = (ev) => {
    if (ev.target === ev.currentTarget) {
      setHoveredDate(null);
    }
  };

  useEffect(() => {
    let newStartDate;
    if (granularity === 'day') {
      newStartDate = moment(endDate).add(1, 'days').toDate();
    } else if (granularity === 'week') {
      newStartDate = moment(endDate).add(7, 'days').toDate();
    } else if (granularity === 'month') {
      newStartDate = moment(endDate).add(1, 'months');
    }
    setClickableRight(newStartDate <= getBaseDate());
  }, [granularity, endDate]);
  useEffect(() => {
    fetchTableData(activeDate, granularity);
  }, [activeDate]);
  useEffect(() => {
    setDay();
  }, []);
  return (
    <div className="wrap_box_type3" onMouseEnter={handleEnterOutside} onMouseLeave={()=>setHoveredDate(null)}>
      <div className="box_blog graph_revenue" style={{padding: '25px 23px'}} onMouseEnter={handleEnterOutside}>
        <div className="wrap_graph">
          <h4 className="screen_out">통계그래프</h4>
          <div className="date_graph" onMouseEnter={handleEnterOutside}>
            <span className="tit_date day">
              {`${activeDate.getFullYear()}. ${activeDate.getMonth() + 1}. ${activeDate.getDate()} `}
              <span className="txt_day">{['일', '월', '화', '수', '목', '금', '토'][activeDate.getDay()]}</span>
            </span>
            <ul className="data_cate">
              <li className={lineOn ? 'on' : ''} onClick={lineToggle}><span
                className="cate_type cate_type_type4">수익</span></li>
              <li className={columnOn ? 'on' : ''} onClick={columnToggle}><span
                className="cate_type cate_type_type5"><span className="ico_blog ico_graph"/>노출수</span>
              </li>
            </ul>
          </div>
          <div className="tab_graph">
            <strong className="screen_out">그래프단위 선택 목록</strong>
            <ul className="list_tab list_tab_type1">
              <li className={granularity === 'day' ? 'on' : ''}>
                <button className="btn_tab" onClick={setDay}><span className="txt_g">일간</span></button>
              </li>
              <li className={granularity === 'week' ? 'on' : ''}>
                <button className="btn_tab" onClick={setWeek}><span className="txt_g">주간</span></button>
              </li>
              <li className={granularity === 'month' ? 'on' : ''}>
                <button className="btn_tab" onClick={setMonth}><span className="txt_g">월간</span></button>
              </li>
            </ul>
          </div>
          <LineColumnChart
            title="애드핏"
            data={graphData}
            startDate={startDate}
            endDate={endDate}
            granularity={granularity}
            color="#fedd2f"
            lineOn={lineOn}
            columnOn={columnOn}
            activeDate={activeDate}
            hoveredDate={hoveredDate}
            setHoveredDate={setHoveredDate}
            clickableRight={clickableRight}
            handleClickDate={handleClickDate}
            handleClickLeft={handleClickLeft}
            handleClickRight={handleClickRight}
          />
        </div>
      </div>
      <h4 className="screen_out">수익 데이타</h4>
      <RevenueTable
        vendor="adfit"
        data={tableData}
      />
      {(!graphData || tableLoading) && !tableError && !graphError &&
      <div className="date_loading">
        <div className="inner_g">
          <span className="ico_blog ico_loading">로딩중입니다.</span>
        </div>
      </div>
      }
      {
        (graphError || tableError) &&
        <div className="statistic_refresh info_none">
          <div className="inner_refresh">
            <span className="wrap_ico"><span className="ico_blog ico_post"/></span>
            <a onClick={setDay} className="link_none">다시 조회하기</a>
          </div>
        </div>
      }
    </div>
  )
};

export default RevenueDetail;